import { EFinancingCompany } from '@if/core/types'
import type { ColumnDef, DataGridPageQueryParams, DataGridPageQueryResult } from '@if/mui'
import { useGetLoanDashboardEntries } from '@src/api/loan/loan-dashboard-api'
import { useCompany } from '@src/services/company-hooks'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { LoanDashboardEntry, LoanDashBoardFiltersSchemaType } from './loan-filters-schema'

const DOTNET_MIN_DATE = '0001-01-01T00:00:00'

export const useLoanDashboardEntries = ({
  filters,
  pagination,
}: DataGridPageQueryParams<
  LoanDashboardEntry,
  LoanDashBoardFiltersSchemaType
>): DataGridPageQueryResult<LoanDashboardEntry> => {
  const financingCompanyId = useCompany()
  return useGetLoanDashboardEntries({
    financingCompanyId,
    searchTerm: filters.searchTerm,
    offset: pagination.offset,
    limit: pagination.limit,
  })
}

export const useLoanListColumns = (): ColumnDef<LoanDashboardEntry>[] => {
  const { t } = useTranslation()
  return useMemo<ColumnDef<LoanDashboardEntry>[]>(
    () => [
      {
        name: 'loanId',
        type: 'reference',
        header: t('loan.loanList.loanId'),
        isAdjusted: true,
      },
      {
        name: 'contractNumber',
        type: 'reference',
        header: t('loan.loanList.contractNumber'),
        href: (row) => `/${row.financingCompanyId}/loans/${row.loanId}`,
        minWidth: 170,
      },
      {
        name: 'creditReferenceNumber',
        header: t('loan.loanList.creditReferenceNumber'),
        minWidth: 140,
      },
      {
        name: 'financingProgramId',
        header: t('loan.loanList.financingProgram'),
        minWidth: 210,
        getValue: (row) => t(`EFinancingProgram.${row.financingProgramId}`, { ns: 'enum' }),
      },
      {
        name: 'financingCompanyId',
        header: t('loan.loanList.financingCompany'),
        minWidth: 210,
        flexGrow: 1,
        getValue: (row) => {
          switch (row.financingCompanyId) {
            case EFinancingCompany.IFinance:
            case EFinancingCompany.Iceberg:
              return row.financingCompanyId
            default:
              return ''
          }
        },
      },
      {
        name: 'borrowerFullName',
        type: 'title',
        header: t('loan.loanList.borrower'),
        minWidth: 200,
        flexGrow: 1,
      },
      {
        name: 'loanStatusId',
        header: t('loan.loanList.loanStatus'),
        minWidth: 150,
        flexGrow: 1,
        getValue: (row) => t(`loan.loanStatuses.${row.loanStatusId}`),
      },
      {
        name: 'createdOn',
        type: 'dateonly',
        header: t('loan.loanList.createdOn'),
        minWidth: 130,
        getValue: (row) => (row.createdOn === DOTNET_MIN_DATE ? null : row.createdOn),
      },
      {
        name: 'loanAmount',
        type: 'currency',
        header: t('loan.loanList.loanAmount'),
        minWidth: 150,
      },
      {
        name: 'loanBalance',
        type: 'currency',
        header: t('loan.loanList.loanBalance'),
        minWidth: 200,
      },
    ],
    [t],
  )
}
